"use client";
// One floating meeting window for the whole tab. It lives in the root layout, so a call keeps running while staff move
// between pages, and it never blocks the page behind it: staff can shrink it to a small window they drag anywhere, or
// minimize it to a bar. The conference element is never re-parented, because moving it would reload the call.
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Copy, Maximize2, Minus, PictureInPicture2, Video, X } from 'lucide-react';
import { MeetingRoom } from '@/components/interview-video-room';

type RoomProps = Parameters<typeof MeetingRoom>[0];
export type OpenMeeting = { title: string; room: RoomProps; invitation: () => Promise<string> };
type Mode = 'large' | 'small' | 'min';

let current: OpenMeeting | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach(listener => listener());
export function openMeetingWindow(meeting: OpenMeeting) { current = meeting; emit(); }
export function closeMeetingWindow() { current = null; emit(); }
const subscribe = (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; };

const SMALL = { w: 400, h: 300 };
function smallSize() { return { w: Math.min(SMALL.w, window.innerWidth - 16), h: Math.min(SMALL.h, window.innerHeight - 16) }; }
function clamp(x: number, y: number, w: number, h: number) {
  return { x: Math.max(8, Math.min(x, window.innerWidth - w - 8)), y: Math.max(8, Math.min(y, window.innerHeight - h - 8)) };
}

export default function MeetingWindowHost() {
  const meeting = useSyncExternalStore(subscribe, () => current, () => null);
  const [mode, setMode] = useState<Mode>('large');
  const [spot, setSpot] = useState<{ x: number; y: number } | null>(null);
  const [notice, setNotice] = useState('');
  const drag = useRef<{ dx: number; dy: number } | null>(null);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => { setMode('large'); setNotice(''); }, [meeting]);
  useEffect(() => {
    if (mode !== 'small') return;
    // First time: bottom-right corner, above the assistant button. Afterwards: where the user left it, kept on screen.
    const keepInside = () => setSpot(prev => { const { w, h } = smallSize(); const el = box.current; return prev ? clamp(prev.x, prev.y, el?.offsetWidth || w, el?.offsetHeight || h) : clamp(window.innerWidth, window.innerHeight - h - 88, w, h); });
    keepInside(); window.addEventListener('resize', keepInside);
    return () => window.removeEventListener('resize', keepInside);
  }, [mode]);
  if (!meeting) return null;

  async function copy() {
    try { const url = await meeting!.invitation(); try { await navigator.clipboard.writeText(url); setNotice('Invitation copied. Share it only with your meeting participants.'); } catch { setNotice(url); } }
    catch (e) { setNotice((e as Error).message); }
  }
  function leave() { if (window.confirm('Leave this meeting?')) closeMeetingWindow(); }
  const startDrag = (e: React.PointerEvent) => {
    if (mode !== 'small' || (e.target as HTMLElement).closest('button') || !box.current) return;
    const rect = box.current.getBoundingClientRect();
    drag.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const moveDrag = (e: React.PointerEvent) => {
    if (!drag.current || !box.current) return;
    setSpot(clamp(e.clientX - drag.current.dx, e.clientY - drag.current.dy, box.current.offsetWidth, box.current.offsetHeight));
  };

  const icon = 'grid h-9 w-9 shrink-0 place-items-center rounded-lg border bg-white text-slate-700 hover:bg-slate-50';
  const size = mode === 'small' ? smallSize() : null;
  const frame = mode === 'large'
    ? 'inset-0 m-auto h-[94dvh] w-[96vw] max-w-6xl rounded-xl max-sm:h-[100dvh] max-sm:w-screen max-sm:rounded-none'
    : mode === 'small' ? 'min-h-[200px] min-w-[260px] resize rounded-xl' : 'bottom-24 right-4 w-[min(360px,calc(100vw-32px))] rounded-2xl';
  const style = mode === 'small' && spot && size ? { left: spot.x, top: spot.y, width: size.w, height: size.h } : undefined;

  return <div ref={box} role="dialog" aria-modal="false" aria-label={`Video meeting: ${meeting.title}`} style={style}
    className={`fixed z-[150] flex flex-col overflow-hidden border bg-white shadow-2xl ${frame}`}>
    <header onPointerDown={startDrag} onPointerMove={moveDrag} onPointerUp={() => { drag.current = null; }}
      className={`flex items-center gap-2 border-b bg-white ${mode === 'large' ? 'flex-wrap p-3' : 'p-2'} ${mode === 'small' ? 'cursor-move touch-none select-none' : ''}`}>
      <h3 className="mr-auto flex min-w-0 items-center gap-2 font-semibold">
        {mode === 'min' ? <span className="relative flex h-2.5 w-2.5 shrink-0"><span className="absolute h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" /><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" /></span> : <Video size={16} className="shrink-0 text-indigo-600" aria-hidden />}
        <span className={mode === 'large' ? 'break-words' : 'truncate text-sm'}>{mode === 'min' ? `In meeting · ${meeting.title}` : meeting.title}</span>
      </h3>
      {mode === 'large' ? <button className="inline-flex items-center gap-1 rounded-lg border px-4 py-2 text-sm font-semibold" onClick={() => void copy()}><Copy size={14} />Copy invitation</button>
        : mode === 'small' ? <button className={icon} aria-label="Copy invitation" title="Copy invitation" onClick={() => void copy()}><Copy size={15} /></button> : null}
      {mode !== 'min' ? <button className={icon} aria-label="Minimize meeting" title="Minimize" onClick={() => setMode('min')}><Minus size={16} /></button> : null}
      {mode !== 'small' ? <button className={icon} aria-label="Small movable window" title="Small window you can move" onClick={() => setMode('small')}><PictureInPicture2 size={16} /></button> : null}
      {mode !== 'large' ? <button className={icon} aria-label="Enlarge meeting" title="Enlarge" onClick={() => setMode('large')}><Maximize2 size={15} /></button> : null}
      <button className={`${icon} text-red-700`} aria-label="Leave and close meeting" title="Leave meeting" onClick={leave}><X size={16} /></button>
    </header>
    {notice && mode !== 'min' ? <p role="status" className="break-all bg-sky-50 px-3 py-2 text-xs text-sky-800">{notice}</p> : null}
    <div className={mode === 'min' ? 'hidden' : 'flex min-h-0 flex-1 flex-col'}><MeetingRoom {...meeting.room} /></div>
  </div>;
}
